import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2, UserCheck, UserX, Users, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function RwOfficersPage() {
  const [officers, setOfficers] = useState([]);
  const [dusuns, setDusuns] = useState([]);
  const [form, setForm] = useState({ nik: "", dusun: "", rw: "" });
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: dz }] = await Promise.all([
        api.get("/rw-officers"),
        api.get("/dusuns"),
      ]);
      setOfficers(list || []);
      setDusuns((dz || []).map((d) => d.nama));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!form.nik || form.nik.length !== 16 || !form.dusun || !form.rw) {
      toast.error("Lengkapi NIK 16 digit, dusun, dan RW.");
      return;
    }
    setAdding(true);
    try {
      const rw = form.rw.padStart(3, "0");
      await api.post("/rw-officers", { nik: form.nik, dusun: form.dusun, rw });
      toast.success("Pengurus RW ditambahkan.");
      setForm({ nik: "", dusun: "", rw: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdding(false);
    }
  };

  const toggleActive = async (o) => {
    try {
      await api.put(`/rw-officers/${o.id}`, { active: !o.active });
      toast.success(o.active ? "Dinonaktifkan." : "Diaktifkan.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`Hapus pengurus ${o.nama} (RW ${o.rw})?`)) return;
    try {
      await api.delete(`/rw-officers/${o.id}`);
      toast.success("Pengurus dihapus.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="rw-officers-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Pengurus RW</h1>
          <p className="text-sm text-slate-600 mt-1">
            Kelola daftar Ketua RW yang dapat login lewat portal <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">/rw</code> menggunakan NIK mereka.
          </p>
        </div>
        <a
          href="/rw"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 border border-slate-300 rounded-md px-3 py-2"
          data-testid="rw-portal-link"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Buka Portal Ketua RW
        </a>
      </header>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
            <Plus className="h-4 w-4" /> Tambah Pengurus RW
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
            <Input
              value={form.nik}
              onChange={(e) => setForm({ ...form, nik: e.target.value.replace(/\D/g, "").slice(0, 16) })}
              placeholder="NIK (16 digit)"
              className="sm:col-span-5 h-10 font-mono"
              inputMode="numeric"
              maxLength={16}
              data-testid="rw-add-nik"
            />
            <select
              value={form.dusun}
              onChange={(e) => setForm({ ...form, dusun: e.target.value })}
              className="sm:col-span-4 h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
              data-testid="rw-add-dusun"
            >
              <option value="">Pilih Dusun...</option>
              {dusuns.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <Input
              value={form.rw}
              onChange={(e) => setForm({ ...form, rw: e.target.value.replace(/\D/g, "").slice(0, 3) })}
              placeholder="RW (cth: 001)"
              className="sm:col-span-2 h-10 font-mono"
              inputMode="numeric"
              maxLength={3}
              data-testid="rw-add-rw"
            />
            <Button
              onClick={add}
              disabled={adding || form.nik.length !== 16 || !form.dusun || !form.rw}
              className="sm:col-span-1 h-10 bg-slate-900 hover:bg-slate-800 disabled:opacity-50"
              data-testid="rw-add-submit"
            >
              {adding ? "..." : "+"}
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Sistem akan mencocokkan NIK ke data pemilih. Nama otomatis terisi dan NIK harus terdaftar di dusun & RW yang sama.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rw-officers-table">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold w-12">No</th>
                  <th className="px-3 py-2 text-left font-semibold">Nama</th>
                  <th className="px-3 py-2 text-left font-semibold">Dusun</th>
                  <th className="px-3 py-2 text-center font-semibold w-16">RW</th>
                  <th className="px-3 py-2 text-left font-semibold">NIK (sebagian)</th>
                  <th className="px-3 py-2 text-center font-semibold w-24">Status</th>
                  <th className="px-3 py-2 text-center font-semibold w-32">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {!loading && officers.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-10 text-slate-500">
                    Belum ada pengurus RW. Tambahkan menggunakan form di atas.
                  </td></tr>
                )}
                {loading && (
                  <tr><td colSpan={7} className="text-center py-10 text-slate-400">Memuat...</td></tr>
                )}
                {officers.map((o, i) => (
                  <tr key={o.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`rw-officer-row-${o.id}`}>
                    <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                    <td className="px-3 py-2 font-medium">{o.nama}</td>
                    <td className="px-3 py-2">{o.dusun}</td>
                    <td className="px-3 py-2 text-center font-mono">{o.rw}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-600">
                      {(o.nik || "").slice(0, 6)}****{(o.nik || "").slice(-4)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {o.active ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                          <UserCheck className="h-3.5 w-3.5" /> Aktif
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-slate-400 text-xs">
                          <UserX className="h-3.5 w-3.5" /> Nonaktif
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex justify-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleActive(o)}
                          className="h-7 px-2 text-xs"
                          data-testid={`rw-toggle-${o.id}`}
                        >
                          {o.active ? "Nonaktifkan" : "Aktifkan"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => remove(o)}
                          className="h-7 px-2 text-red-600 hover:bg-red-50"
                          data-testid={`rw-delete-${o.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
